import { Controller } from '@hotwired/stimulus'
import { ProgressBar } from '../../api/models/types'
import {
  connectFlowValue,
  getComponentContext,
  getFlow,
  lifecycleTrigger,
} from '../../helpers/controllerHelpers/utils'
import { debounce } from '../../helpers/debounce'

export default class extends Controller {
  static targets = ['bar', 'reset', 'text', 'svg', 'transparentCircle', 'fillCircle']
  declare barTarget: HTMLElement
  declare hasBarTarget: boolean
  declare textTarget: HTMLElement
  declare hasTextTarget: boolean
  declare transparentCircleTarget: HTMLElement
  declare hasTransparentCircleTarget: boolean
  declare fillCircleTarget: HTMLElement
  declare hasFillCircleTarget: boolean

  static values = {
    barType: String,
    percentType: String,
    startAtOne: String,
    completeText: String,
    percent: Number,
    size: Number,
    barColor: String,
  }
  declare barTypeValue: string
  declare percentTypeValue: string
  declare startAtOneValue: string
  declare completeTextValue: string
  declare percentValue: number

  debounceCalculatePercent: (...args: any[]) => void
  disconnectPageId?: () => void
  maxPages: number
  currentPageIndex: number

  hasIconTarget: boolean
  iconTarget: HTMLElement
  component: ProgressBar
  flowEl: Element | null

  connect() {
    this.debounceCalculatePercent = debounce(this.calculatePercent, 250, true)

    this.disconnectPageId = connectFlowValue(
      this,
      { attributeName: 'data-flow-page-id-value' },
      this.pageIdValueChanged
    )
    const { component, flowEl } = getComponentContext(this)
    this.component = component as ProgressBar
    this.flowEl = flowEl
    lifecycleTrigger(this, 'mounted')
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    // if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
    if (typeof this.disconnectPageId === 'function') this.disconnectPageId()
  }

  pageIdValueChanged(id: string | number) {
    const pages = getFlow(this).pages || []
    const pageIndex = pages.findIndex((p) => p.id === id)
    this.maxPages = pages.length || 1
    this.currentPageIndex = pageIndex || 0
    if (typeof this.debounceCalculatePercent === 'function') this.debounceCalculatePercent()
    else this.calculatePercent()
  }

  percentValueChanged(percent: number) {
    this.barTypeValue === 'circle' ? this.adjustBarPercentage(percent) : this.adjustWidth(percent)
  }

  /* Circular bar watchers */
  barColorValueChanged(barColor: string) {
    if (this.hasFillCircleTarget) {
      this.fillCircleTarget.setAttribute('stroke', barColor)
    }
  }

  sizeValueChanged(size: number) {
    const halfSize = String(size / 2)
    const radius = String(size / 2 - 1.8)
    if (this.hasTransparentCircleTarget) {
      this.transparentCircleTarget.setAttribute('r', radius)
      this.transparentCircleTarget.setAttribute('cx', halfSize)
      this.transparentCircleTarget.setAttribute('cy', halfSize)
    }
    if (this.hasFillCircleTarget) {
      this.fillCircleTarget.setAttribute('r', radius)
      this.fillCircleTarget.setAttribute('cx', halfSize)
      this.fillCircleTarget.setAttribute('cy', halfSize)
    }
  }

  /* General Methods */
  resetUserData() {
    this.dispatch('reset')
  }

  calculatePercent() {
    const currentPageIndexDisplay =
      this.startAtOneValue === 'true' ? this.currentPageIndex + 1 : this.currentPageIndex
    const currentTotalPages = this.maxPages

    this.percentValue = Math.min(
      Math.round((100 * currentPageIndexDisplay) / currentTotalPages),
      100
    )
  }

  adjustWidth(percent: number) {
    if (this.hasBarTarget) this.barTarget.style.width = `${percent}%`
    if (this.hasTextTarget) {
      if (+percent > 10) this.textTarget.classList.add('altColor')
      else this.textTarget.classList.remove('altColor')

      if (this.currentPageIndex !== undefined && this.maxPages !== undefined) {
        const text = this.getPercentText(percent)
        this.textTarget.innerText = text
      }
    }
    if (this.hasIconTarget) {
      this.iconTarget.className = `reset percent-${percent}`
    }
  }

  adjustBarPercentage(percent: number) {
    if (this.hasFillCircleTarget) {
      this.fillCircleTarget.style.strokeDashoffset = `${100 - percent}`
    }
    if (this.hasTextTarget) {
      const text = this.getPercentText(percent)
      this.textTarget.innerText = text
    }
  }

  getPercentText(percent: number) {
    switch (this.percentTypeValue) {
      case 'fractional': {
        if (this.startAtOneValue) {
          const pageIndexOneBased = this.currentPageIndex + 1
          return pageIndexOneBased === this.maxPages
            ? this.completeTextValue || ''
            : `${pageIndexOneBased} / ${this.maxPages > 1 ? this.maxPages - 1 : 1}`
        }
        return `${this.currentPageIndex} / ${this.maxPages > 1 ? this.maxPages - 1 : 1}`
      }

      default: {
        return `${percent}% complete`
      }
    }
  }
}
